/**
 * Redaction for log events — a TypeScript port of
 * `wrango-backend-utils/src/wrango_utils/logging/_redact.py`.
 *
 * Two surfaces, matching the Python module:
 *
 * - {@link redactEntry} — the blacklist-only pass applied to a whole log entry
 *   before serialization (mirrors the `redact_sensitive_values` structlog
 *   processor). Scrubs known sensitive keys + Bearer/Basic token strings, and
 *   never touches normal operational fields (`status_code`, `duration_ms`, …).
 * - {@link redact} + {@link RedactionPolicy} — a reusable engine for one value
 *   (a request/response body, a header map) with either a **blacklist**
 *   (allow-by-default, the default) or a **whitelist** (redact-by-default — the
 *   PHI lever).
 *
 * Isomorphic: no Node/DOM dependencies, so the browser tier can reuse it before
 * writing to the console.
 */

export const REDACTED = "[REDACTED]";

/**
 * Normalize a key for matching: lowercase and strip `_`/`-`, so snake_case,
 * camelCase, and kebab-case all collapse to the same form
 * (`access_token` == `accessToken` == `access-token`). This is what makes the
 * redactor catch browser payloads (camelCase) as well as the Python services'
 * snake_case — without it, `secretKey`/`accessToken` slip through.
 */
function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[_-]/g, "");
}

/**
 * Readable source list (snake_case) of keys whose values are always fully
 * redacted, at any depth. Mirrors `_SENSITIVE_KEYS` in `_redact.py`, plus
 * additions found in browser payloads (`secret_key`, `session_token`, …) and the
 * cookie / Clerk credentials that flow through the BFF. Matched in normalized
 * form — see {@link normalizeKey}.
 */
const SENSITIVE_KEY_SOURCE = [
  "access_token",
  "refresh_token",
  "session_token",
  "id_token",
  "token",
  "secret",
  "secret_key",
  "client_secret",
  "password",
  "authorization",
  "x-internal-secret",
  "credentials",
  "encrypted_credentials",
  "developer_token",
  "api_key",
  "access_key",
  "secret_access_key",
  // Provider API-key headers use an `x-` prefix (OpenX `x-apikey`, AWS-style
  // `x-api-key`); `api_key` normalizes to `apikey` and does NOT cover them.
  // normalizeKey strips `-`/`_`, so this one entry covers both variants.
  "x-apikey",
  "private_key",
  // Cookies carry live bearer credentials: Clerk's `__session` (and
  // `__clerk_db_jwt` in dev) session JWTs ride in the `cookie` / `set-cookie`
  // headers, which the request logger captures wholesale. The Bearer/DSN scrubs
  // can't reach a `__session=eyJ…` value, so redact the whole header by key.
  "cookie",
  "set-cookie",
  // Clerk + webhook secrets and tokens. camelCase forms (`secretKey`,
  // `sessionToken`) already normalize into the entries above; these are the
  // remaining literal names.
  "clerk_secret_key",
  "clerk_webhook_secret",
  "webhook_secret",
  "signing_secret",
  "clerk_db_jwt",
  "rotating_token",
  "jwt",
  // Clerk sets these on the request its Next middleware handled: `x-clerk-auth-token` is the
  // live session JWT (a bearer credential), `x-clerk-request-data` an encrypted request-context
  // blob. Exact-key matching means `token` / `jwt` don't cover the hyphenated header names, so
  // list them explicitly. (The benign `x-clerk-auth-status` / `-reason` stay visible for debugging.)
  "x-clerk-auth-token",
  "x-clerk-request-data",
  // Uncomment to also redact a standalone `session`/`__session` field (the JWT is already covered via the cookie header; on = safety even if it lands in a body, at the cost of nuking benign `session` objects).
  // "session",
  // Uncomment to scrub Clerk/Svix webhook verification headers (the signature is derived from the signing secret, not itself secret; on = keep raw signatures out of logs entirely).
  // "svix-signature",
  // "svix-id",
  // "svix-timestamp",
  // Connection strings — a value under any of these embeds a password in the
  // clear (e.g. `postgresql://user:pass@host/db`); redact wholesale.
  "database_url",
  "rabbitmq_url",
  "amqp_url",
  "dsn",
  "connection_string",
];

/** Sensitive keys in normalized form (see {@link normalizeKey}). */
export const SENSITIVE_KEYS: ReadonlySet<string> = new Set(
  SENSITIVE_KEY_SOURCE.map(normalizeKey),
);

/** `Bearer <token>` / `Basic <token>` in any string value (case-insensitive). */
const BEARER_RE = /(Bearer|Basic)\s+\S+/gi;

/**
 * Mask only the `user:password@` password segment of a connection URL embedded
 * anywhere in a string, keeping scheme/user/host:
 * `postgresql+asyncpg://user:hunter2@host/db` -> `postgresql+asyncpg://user:[REDACTED]@host/db`.
 */
const DSN_PASSWORD_RE = /([a-zA-Z][a-zA-Z0-9+.\-]*:\/\/[^:/?#@\s]+:)[^@/?#\s]+(@)/g;

/** Mechanisms 2 + 3 from `_redact_blacklist`: Bearer/Basic scrub, then DSN mask. */
function scrubString(value: string): string {
  const bearerMasked = value.replace(BEARER_RE, "$1 " + REDACTED);
  return bearerMasked.replace(DSN_PASSWORD_RE, "$1" + REDACTED + "$2");
}

/**
 * True only for "plain" objects (record-like), mirroring Python's `isinstance(v, dict)`.
 * Class instances, Date, Map, etc. fall through unchanged like Python's `else` branch.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object") return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

export type RedactionMode = "blacklist" | "whitelist";

/** How to redact one logged value. See `RedactionPolicy` in `_redact.py`. */
export interface RedactionPolicy {
  /**
   * `blacklist` (default, allow-by-default): redact only keys in
   * `SENSITIVE_KEYS ∪ keys` and Bearer/Basic token strings; everything else
   * passes. `whitelist` (redact-by-default, the PHI lever): any key NOT in
   * `keys` collapses its whole subtree to `[REDACTED]`.
   */
  mode: RedactionMode;
  /** Extra deny keys (blacklist) or the allow-set (whitelist). Matched case-insensitively. */
  keys: ReadonlySet<string>;
  /**
   * Whitelist only: when the top-level body is a non-object (a raw non-JSON
   * string, or a bare array/scalar), collapse it to `[REDACTED]`. `null` always
   * passes through. Set `false` to log non-object bodies as-is.
   */
  redactUnparsed: boolean;
}

/** Convenience default: the historical blacklist behaviour (no extra keys). */
export const BLACKLIST: RedactionPolicy = {
  mode: "blacklist",
  keys: new Set(),
  redactUnparsed: true,
};

/** Recursively redact `denyKeys` + Bearer/Basic token strings (allow-by-default). */
function redactBlacklist(value: unknown, denyKeys: ReadonlySet<string>): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => redactBlacklist(item, denyKeys));
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = denyKeys.has(normalizeKey(k)) ? REDACTED : redactBlacklist(v, denyKeys);
    }
    return out;
  }
  if (typeof value === "string") {
    return scrubString(value);
  }
  return value;
}

/**
 * Recursively keep only `allowKeys` (redact-by-default); non-allowed keys
 * collapse. Scalars (incl. strings) pass unchanged — a non-object *body* is
 * handled by {@link redact} before it reaches here.
 */
function redactWhitelist(value: unknown, allowKeys: ReadonlySet<string>): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => redactWhitelist(item, allowKeys));
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = allowKeys.has(normalizeKey(k)) ? redactWhitelist(v, allowKeys) : REDACTED;
    }
    return out;
  }
  return value;
}

/** Redact `value` per `policy` (see {@link RedactionPolicy}). */
export function redact(value: unknown, policy: RedactionPolicy = BLACKLIST): unknown {
  if (policy.mode === "whitelist") {
    // Only an object can be key-filtered. Any non-object top-level body has no
    // keys to whitelist, so redact it wholesale (else scalar elements sail
    // through). `null`/`undefined` always pass.
    if (!isPlainObject(value)) {
      return policy.redactUnparsed && value !== null && value !== undefined ? REDACTED : value;
    }
    return redactWhitelist(value, new Set<string>([...policy.keys].map(normalizeKey)));
  }
  const denyKeys =
    policy.keys.size === 0
      ? SENSITIVE_KEYS
      : new Set<string>([...SENSITIVE_KEYS, ...[...policy.keys].map(normalizeKey)]);
  return redactBlacklist(value, denyKeys);
}

/**
 * Blacklist-only scrub of a whole log entry, applied as the final step before
 * serialization. Mirrors the `redact_sensitive_values` structlog processor: a
 * top-level sensitive key is fully redacted; every other value is recursively
 * blacklist-redacted. Uses only the base {@link SENSITIVE_KEYS}.
 */
export function redactEntry(entry: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(entry)) {
    out[k] = SENSITIVE_KEYS.has(normalizeKey(k)) ? REDACTED : redactBlacklist(v, SENSITIVE_KEYS);
  }
  return out;
}
